import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import AuthorizationPanel from "./AuthorizationPanel";

const JOB_STATUSES = [
  "new", "preliminary_review", "needs_info", "inspection_scheduled",
  "estimate_in_progress", "estimate_sent", "customer_considering",
  "authorization_ready", "authorization_sent", "customer_authorized",
  "deposit_required", "deposit_received", "scheduled", "vehicle_received",
  "in_repair", "supplement_pending", "ready_for_pickup", "completed",
  "declined", "archived",
];

const PROJECT_TYPES = [
  ["collision_insurance", "Collision / Insurance"],
  ["custom_jdm_specialty", "Custom / JDM / Specialty"],
  ["warranty", "Warranty"],
  ["detail", "Detail"],
  ["other", "Other"],
];

export default function CrmTab() {
  const [view, setView] = useState("customers"); // customers | vehicles | jobs
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [q, setQ] = useState("");
  const [showAddCust, setShowAddCust] = useState(false);
  const [showAddVeh, setShowAddVeh] = useState(false);
  const [showAddJob, setShowAddJob] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, v, j] = await Promise.all([
        api.get("/admin/crm/customers"),
        api.get("/admin/crm/vehicles"),
        api.get("/admin/crm/jobs"),
      ]);
      setCustomers(c.data || []);
      setVehicles(v.data || []);
      setJobs(j.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const filteredCustomers = useMemo(() => {
    if (!q) return customers;
    const s = q.toLowerCase();
    return customers.filter((c) =>
      (c.full_name || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s) ||
      (c.phone || "").includes(s) ||
      (c.reference || "").toLowerCase().includes(s)
    );
  }, [customers, q]);

  const filteredJobs = useMemo(() => {
    if (!q) return jobs;
    const s = q.toLowerCase();
    return jobs.filter((j) =>
      (j.reference || "").toLowerCase().includes(s) ||
      (j.customer_name || "").toLowerCase().includes(s) ||
      (j.vehicle_summary || "").toLowerCase().includes(s) ||
      (j.scope_summary || "").toLowerCase().includes(s)
    );
  }, [jobs, q]);

  const filteredVehicles = useMemo(() => {
    if (!q) return vehicles;
    const s = q.toLowerCase();
    return vehicles.filter((v) =>
      (v.make || "").toLowerCase().includes(s) ||
      (v.model || "").toLowerCase().includes(s) ||
      (v.vin || "").toLowerCase().includes(s) ||
      (v.customer_name || "").toLowerCase().includes(s)
    );
  }, [vehicles, q]);

  return (
    <div data-testid="crm-tab">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-6">
          <h1 className="font-display uppercase text-3xl">CRM</h1>
          <nav className="flex items-center gap-4">
            {[["customers", "Customers", customers.length], ["vehicles", "Vehicles", vehicles.length], ["jobs", "Jobs", jobs.length]].map(([k, l, n]) => (
              <button key={k} onClick={() => { setView(k); setSelectedCust(null); setSelectedJob(null); }}
                className={`font-mono text-[11px] tracking-[0.24em] uppercase ${view === k ? "text-proform-red" : "text-chrome-300 hover:text-chrome-100"}`}
                data-testid={`crm-nav-${k}`}
              >
                {l} <span className="text-chrome-300">({n})</span>
              </button>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="field py-2" data-testid="crm-search" />
          {view === "customers" && <button className="btn btn-primary" onClick={() => setShowAddCust(true)} data-testid="crm-add-customer-btn">+ Add Customer</button>}
          {view === "vehicles" && <button className="btn btn-primary" onClick={() => setShowAddVeh(true)} data-testid="crm-add-vehicle-btn">+ Add Vehicle</button>}
          {view === "jobs" && <button className="btn btn-primary" onClick={() => setShowAddJob(true)} data-testid="crm-add-job-btn">+ Create Job</button>}
        </div>
      </div>

      {loading && <div className="label">Loading…</div>}

      {view === "customers" && (
        <div className="grid lg:grid-cols-[1fr,420px] gap-8">
          <div className="border border-ink-700 overflow-x-auto">
            <table className="w-full text-sm" data-testid="crm-customers-table">
              <thead className="bg-ink-900">
                <tr className="label border-b border-ink-700">
                  <th className="p-3 text-left">Reference</th>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Contact</th>
                  <th className="p-3 text-left">City</th>
                  <th className="p-3 text-left">Added</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((c) => (
                  <tr key={c.id} onClick={() => setSelectedCust(c)}
                    className={`border-b border-ink-700 cursor-pointer hover:bg-ink-900 ${selectedCust?.id === c.id ? "bg-ink-800" : ""}`}
                    data-testid={`crm-customer-row-${c.reference}`}
                  >
                    <td className="p-3 font-mono text-proform-red whitespace-nowrap">{c.reference}</td>
                    <td className="p-3">{c.full_name}</td>
                    <td className="p-3 text-chrome-300 text-xs">{c.email || "—"}<br />{c.phone}</td>
                    <td className="p-3 text-chrome-300">{c.city || "—"}</td>
                    <td className="p-3 text-chrome-300 whitespace-nowrap">{c.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {filteredCustomers.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-chrome-300">No customers yet — click "+ Add Customer" to create one.</td></tr>}
              </tbody>
            </table>
          </div>
          <CustomerDetail customerId={selectedCust?.id} onChange={loadAll} onOpenJob={(j) => { setView("jobs"); setSelectedJob(j); }} />
        </div>
      )}

      {view === "vehicles" && (
        <div className="border border-ink-700 overflow-x-auto">
          <table className="w-full text-sm" data-testid="crm-vehicles-table">
            <thead className="bg-ink-900">
              <tr className="label border-b border-ink-700">
                <th className="p-3 text-left">Reference</th>
                <th className="p-3 text-left">Vehicle</th>
                <th className="p-3 text-left">Owner</th>
                <th className="p-3 text-left">VIN / Plate</th>
                <th className="p-3 text-left">Added</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((v) => (
                <tr key={v.id} className="border-b border-ink-700 hover:bg-ink-900" data-testid={`crm-vehicle-row-${v.reference}`}>
                  <td className="p-3 font-mono text-proform-red whitespace-nowrap">{v.reference}</td>
                  <td className="p-3">{v.year} {v.make} {v.model} {v.trim && <span className="text-chrome-300">· {v.trim}</span>}</td>
                  <td className="p-3">{v.customer_name}</td>
                  <td className="p-3 text-chrome-300 text-xs font-mono">{v.vin || "—"}<br />{v.license_plate ? `${v.license_plate} ${v.plate_state || ""}` : ""}</td>
                  <td className="p-3 text-chrome-300 whitespace-nowrap">{v.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {filteredVehicles.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-chrome-300">No vehicles yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === "jobs" && (
        <div className="grid lg:grid-cols-[1fr,460px] gap-8">
          <div className="border border-ink-700 overflow-x-auto">
            <table className="w-full text-sm" data-testid="crm-jobs-table">
              <thead className="bg-ink-900">
                <tr className="label border-b border-ink-700">
                  <th className="p-3 text-left">Reference</th>
                  <th className="p-3 text-left">Customer</th>
                  <th className="p-3 text-left">Vehicle</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((j) => (
                  <tr key={j.id} onClick={() => setSelectedJob(j)}
                    className={`border-b border-ink-700 cursor-pointer hover:bg-ink-900 ${selectedJob?.id === j.id ? "bg-ink-800" : ""}`}
                    data-testid={`crm-job-row-${j.reference}`}
                  >
                    <td className="p-3 font-mono text-proform-red whitespace-nowrap">{j.reference}</td>
                    <td className="p-3">{j.customer_name}</td>
                    <td className="p-3">{j.vehicle_summary}</td>
                    <td className="p-3"><span className="label">{j.status}</span></td>
                    <td className="p-3 text-chrome-300 whitespace-nowrap">{j.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {filteredJobs.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-chrome-300">No jobs yet — click "+ Create Job" to create one.</td></tr>}
              </tbody>
            </table>
          </div>
          <JobDetail jobId={selectedJob?.id} onChange={loadAll} />
        </div>
      )}

      {showAddCust && <AddCustomerModal onClose={() => setShowAddCust(false)} onCreated={(c) => { loadAll(); setSelectedCust(c); setView("customers"); }} />}
      {showAddVeh && <AddVehicleModal customers={customers} onClose={() => setShowAddVeh(false)} onCreated={() => loadAll()} />}
      {showAddJob && <AddJobModal customers={customers} vehicles={vehicles} onClose={() => setShowAddJob(false)} onCreated={(j) => { loadAll(); setSelectedJob(j); setView("jobs"); }} />}
    </div>
  );
}

// ---------------- Add Customer Modal ----------------
function AddCustomerModal({ onClose, onCreated }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", preferred_contact: "text", address_line1: "", city: "", state: "CO", zip_code: "", referral_source: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [error, setError] = useState(null);
  const u = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async (force = false) => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/admin/crm/customers", { ...f, force_create: force });
      onCreated(data); onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.existing_customer) {
        setDup(detail.existing_customer);
      } else {
        setError(typeof detail === "string" ? detail : "Could not create customer");
      }
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Add Customer" testid="crm-add-customer-modal">
      <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input required className="field" placeholder="First name *" value={f.first_name} onChange={(e) => u("first_name", e.target.value)} data-testid="cust-first" />
          <input required className="field" placeholder="Last name *" value={f.last_name} onChange={(e) => u("last_name", e.target.value)} data-testid="cust-last" />
          <input type="email" className="field col-span-2" placeholder="Email" value={f.email} onChange={(e) => u("email", e.target.value)} data-testid="cust-email" />
          <input required className="field col-span-2" placeholder="Phone *" value={f.phone} onChange={(e) => u("phone", e.target.value)} data-testid="cust-phone" />
          <select className="field col-span-2" value={f.preferred_contact} onChange={(e) => u("preferred_contact", e.target.value)}>
            <option value="text">Prefers text</option>
            <option value="phone">Prefers phone</option>
            <option value="email">Prefers email</option>
          </select>
          <input className="field col-span-2" placeholder="Address" value={f.address_line1} onChange={(e) => u("address_line1", e.target.value)} />
          <input className="field" placeholder="City" value={f.city} onChange={(e) => u("city", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <input className="field" placeholder="State" value={f.state} onChange={(e) => u("state", e.target.value.toUpperCase())} />
            <input className="field" placeholder="ZIP" value={f.zip_code} onChange={(e) => u("zip_code", e.target.value)} />
          </div>
          <input className="field col-span-2" placeholder="How did they find us? (referral source)" value={f.referral_source} onChange={(e) => u("referral_source", e.target.value)} />
          <textarea rows={2} className="field col-span-2" placeholder="Internal notes" value={f.notes} onChange={(e) => u("notes", e.target.value)} />
        </div>

        {dup && (
          <div className="border border-proform-red/60 bg-proform-red/10 text-chrome-100 p-3 text-sm" data-testid="cust-dup-warning">
            <div className="label text-proform-red mb-1">Possible duplicate</div>
            <div>A customer with this phone/email already exists: <span className="font-mono text-proform-red">{dup.reference}</span> — {dup.full_name} ({dup.email || dup.phone})</div>
            <div className="mt-2 flex gap-3">
              <button type="button" className="btn" onClick={onClose}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => submit(true)} disabled={busy}>Create anyway</button>
            </div>
          </div>
        )}
        {error && <div className="border border-proform-red bg-proform-red/10 text-proform-red p-3 text-sm">{error}</div>}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn">Cancel</button>
          <button type="submit" disabled={busy || !f.first_name || !f.last_name || !f.phone} className="btn btn-primary" data-testid="cust-submit">{busy ? "Saving…" : "Create Customer"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------- Add Vehicle Modal ----------------
function AddVehicleModal({ customers, onClose, onCreated }) {
  const [f, setF] = useState({ customer_id: "", year: "", make: "", model: "", trim: "", color: "", vin: "", license_plate: "", plate_state: "CO", mileage: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [error, setError] = useState(null);
  const u = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async (force = false) => {
    if (!f.customer_id) { setError("Select a customer first"); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/admin/crm/vehicles", { ...f, force_create: force });
      onCreated(data); onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.existing_vehicle) {
        setDup(detail.existing_vehicle);
      } else {
        setError(typeof detail === "string" ? detail : "Could not create vehicle");
      }
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Add Vehicle" testid="crm-add-vehicle-modal">
      <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="space-y-3">
        <select required className="field" value={f.customer_id} onChange={(e) => u("customer_id", e.target.value)} data-testid="veh-customer">
          <option value="">Select customer *</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name} — {c.phone}</option>)}
        </select>
        <div className="grid grid-cols-3 gap-3">
          <input required className="field" placeholder="Year *" value={f.year} onChange={(e) => u("year", e.target.value)} data-testid="veh-year" />
          <input required className="field" placeholder="Make *" value={f.make} onChange={(e) => u("make", e.target.value)} data-testid="veh-make" />
          <input required className="field" placeholder="Model *" value={f.model} onChange={(e) => u("model", e.target.value)} data-testid="veh-model" />
          <input className="field" placeholder="Trim" value={f.trim} onChange={(e) => u("trim", e.target.value)} />
          <input className="field" placeholder="Color" value={f.color} onChange={(e) => u("color", e.target.value)} />
          <input className="field" placeholder="Mileage" value={f.mileage} onChange={(e) => u("mileage", e.target.value)} />
          <input className="field col-span-3" placeholder="VIN" value={f.vin} onChange={(e) => u("vin", e.target.value.toUpperCase())} data-testid="veh-vin" />
          <input className="field col-span-2" placeholder="License Plate" value={f.license_plate} onChange={(e) => u("license_plate", e.target.value.toUpperCase())} />
          <input className="field" placeholder="State" value={f.plate_state} onChange={(e) => u("plate_state", e.target.value.toUpperCase())} />
          <textarea rows={2} className="field col-span-3" placeholder="Vehicle notes / modifications" value={f.notes} onChange={(e) => u("notes", e.target.value)} />
        </div>

        {dup && (
          <div className="border border-proform-red/60 bg-proform-red/10 p-3 text-sm text-chrome-100" data-testid="veh-dup-warning">
            <div className="label text-proform-red mb-1">Duplicate VIN</div>
            <div>Another vehicle uses this VIN: <span className="font-mono text-proform-red">{dup.reference}</span> — {dup.year} {dup.make} {dup.model} (owner: {dup.customer_name})</div>
            <div className="mt-2 flex gap-3">
              <button type="button" className="btn" onClick={onClose}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => submit(true)} disabled={busy}>Create anyway</button>
            </div>
          </div>
        )}
        {error && <div className="border border-proform-red bg-proform-red/10 text-proform-red p-3 text-sm">{error}</div>}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn">Cancel</button>
          <button type="submit" disabled={busy || !f.customer_id} className="btn btn-primary" data-testid="veh-submit">{busy ? "Saving…" : "Create Vehicle"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------- Add Job Modal ----------------
function AddJobModal({ customers, vehicles, onClose, onCreated }) {
  const [f, setF] = useState({
    customer_id: "", vehicle_id: "", project_type: "collision_insurance",
    scope_summary: "", status: "new", insurance_company: "", claim_number: "",
    date_of_loss: "", initial_estimate_amount: "", priority: "normal", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const u = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const customerVehicles = vehicles.filter((v) => v.customer_id === f.customer_id);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload = {
        ...f,
        initial_estimate_amount: f.initial_estimate_amount === "" ? null : Number(f.initial_estimate_amount),
      };
      const { data } = await api.post("/admin/crm/jobs", payload);
      onCreated(data); onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not create job");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Create Job" testid="crm-add-job-modal">
      <form onSubmit={submit} className="space-y-3">
        <select required className="field" value={f.customer_id} onChange={(e) => { u("customer_id", e.target.value); u("vehicle_id", ""); }} data-testid="job-customer">
          <option value="">Select customer *</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name} — {c.phone}</option>)}
        </select>
        <select required className="field" value={f.vehicle_id} onChange={(e) => u("vehicle_id", e.target.value)} disabled={!f.customer_id} data-testid="job-vehicle">
          <option value="">{f.customer_id ? (customerVehicles.length ? "Select vehicle *" : "No vehicles for this customer — add one first") : "Choose customer first"}</option>
          {customerVehicles.map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model}{v.vin ? ` · VIN ${v.vin.slice(-6)}` : ""}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <select className="field" value={f.project_type} onChange={(e) => u("project_type", e.target.value)}>
            {PROJECT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="field" value={f.status} onChange={(e) => u("status", e.target.value)}>
            {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <textarea required rows={3} className="field" placeholder="Scope summary *" value={f.scope_summary} onChange={(e) => u("scope_summary", e.target.value)} data-testid="job-scope" />
        <div className="grid grid-cols-2 gap-3">
          <input className="field" placeholder="Insurance company" value={f.insurance_company} onChange={(e) => u("insurance_company", e.target.value)} />
          <input className="field" placeholder="Claim #" value={f.claim_number} onChange={(e) => u("claim_number", e.target.value)} />
          <input type="date" className="field" placeholder="Date of loss" value={f.date_of_loss} onChange={(e) => u("date_of_loss", e.target.value)} />
          <input type="number" step="0.01" className="field" placeholder="Initial estimate amount" value={f.initial_estimate_amount} onChange={(e) => u("initial_estimate_amount", e.target.value)} />
          <select className="field col-span-2" value={f.priority} onChange={(e) => u("priority", e.target.value)}>
            <option value="low">Low priority</option>
            <option value="normal">Normal priority</option>
            <option value="high">High priority</option>
          </select>
        </div>
        <textarea rows={2} className="field" placeholder="Internal notes" value={f.notes} onChange={(e) => u("notes", e.target.value)} />

        {error && <div className="border border-proform-red bg-proform-red/10 text-proform-red p-3 text-sm">{error}</div>}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn">Cancel</button>
          <button type="submit" disabled={busy || !f.customer_id || !f.vehicle_id || !f.scope_summary} className="btn btn-primary" data-testid="job-submit">{busy ? "Saving…" : "Create Job"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------- Customer Detail Sidebar ----------------
function CustomerDetail({ customerId, onChange, onOpenJob }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    if (!customerId) { setC(null); return; }
    api.get(`/admin/crm/customers/${customerId}`).then((r) => setC(r.data));
  }, [customerId]);

  if (!customerId) return <aside className="border border-ink-700 p-6 h-fit sticky top-24 text-chrome-300 text-sm">Select a customer to view details, vehicles, and jobs.</aside>;
  if (!c) return <aside className="border border-ink-700 p-6 h-fit sticky top-24 label">Loading…</aside>;

  return (
    <aside className="border border-ink-700 p-6 h-fit sticky top-24" data-testid="crm-customer-detail">
      <div className="label">Reference</div>
      <div className="font-display text-3xl text-proform-red">{c.reference}</div>
      <div className="mt-3 text-sm">
        <div className="font-display text-xl">{c.full_name}</div>
        <div className="text-chrome-300">{c.email || "—"} · <a href={`tel:${c.phone}`} className="hover:text-proform-red">{c.phone}</a></div>
        <div className="text-chrome-300 text-xs mt-1">Prefers {c.preferred_contact}{c.city ? ` · ${c.city}${c.state ? `, ${c.state}` : ""}` : ""}</div>
        {c.referral_source && <div className="text-chrome-300 text-xs mt-1">Source: {c.referral_source}</div>}
        {c.notes && <div className="text-chrome-100 text-xs mt-2 whitespace-pre-line">{c.notes}</div>}
      </div>

      <div className="mt-6">
        <div className="label mb-2">Vehicles ({c.vehicles?.length || 0})</div>
        <div className="space-y-2">
          {(c.vehicles || []).map((v) => (
            <div key={v.id} className="border border-ink-700 p-2 text-sm">
              <div>{v.year} {v.make} {v.model}</div>
              <div className="text-chrome-300 text-xs font-mono">{v.vin || "—"}</div>
            </div>
          ))}
          {!c.vehicles?.length && <div className="text-chrome-300 text-xs">No vehicles</div>}
        </div>
      </div>

      <div className="mt-6">
        <div className="label mb-2">Jobs ({c.jobs?.length || 0})</div>
        <div className="space-y-2">
          {(c.jobs || []).map((j) => (
            <button key={j.id} onClick={() => onOpenJob && onOpenJob(j)} className="w-full text-left border border-ink-700 p-2 text-sm hover:border-proform-red block">
              <div className="flex justify-between"><span className="font-mono text-proform-red">{j.reference}</span><span className="label">{j.status}</span></div>
              <div className="text-chrome-300 text-xs mt-1">{j.vehicle_summary}</div>
            </button>
          ))}
          {!c.jobs?.length && <div className="text-chrome-300 text-xs">No jobs</div>}
        </div>
      </div>
    </aside>
  );
}

// ---------------- Job Detail Sidebar ----------------
function JobDetail({ jobId, onChange }) {
  const [j, setJ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    if (!jobId) { setJ(null); return; }
    const r = await api.get(`/admin/crm/jobs/${jobId}`);
    setJ(r.data);
  };
  useEffect(() => { load(); }, [jobId]);

  if (!jobId) return <aside className="border border-ink-700 p-6 h-fit sticky top-24 text-chrome-300 text-sm">Select a job to view details, update status, and send a repair authorization.</aside>;
  if (!j) return <aside className="border border-ink-700 p-6 h-fit sticky top-24 label">Loading…</aside>;

  const updateStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/admin/crm/jobs/${j.id}`, { status }); await load(); onChange && onChange(); }
    finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.patch(`/admin/crm/jobs/${j.id}`, { note }); setNote(""); await load(); }
    finally { setBusy(false); }
  };

  return (
    <aside className="border border-ink-700 p-6 h-fit sticky top-24" data-testid="crm-job-detail">
      <div className="label">Job Reference</div>
      <div className="font-display text-3xl text-proform-red">{j.reference}</div>
      <div className="mt-2 text-sm">
        <div><span className="label">Customer</span><div>{j.customer_name}</div></div>
        <div className="mt-2"><span className="label">Vehicle</span><div>{j.vehicle_summary}</div></div>
        <div className="mt-2"><span className="label">Scope</span><div className="text-chrome-100 whitespace-pre-line">{j.scope_summary}</div></div>
        {(j.insurance_company || j.claim_number) && (
          <div className="mt-2"><span className="label">Insurance</span><div>{j.insurance_company || "—"} · claim {j.claim_number || "—"}</div></div>
        )}
        <div className="mt-2 grid grid-cols-2 gap-3">
          <div><span className="label">Priority</span><div className="text-chrome-100">{j.priority}</div></div>
          <div><span className="label">Est. amount</span><div className="text-chrome-100">{j.initial_estimate_amount != null ? `$${Number(j.initial_estimate_amount).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}` : "—"}</div></div>
        </div>
      </div>

      <div className="mt-6">
        <div className="label mb-2">Update status</div>
        <select className="field" value={j.status} onChange={(e) => updateStatus(e.target.value)} disabled={busy} data-testid="crm-job-status">
          {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-4">
        <div className="label mb-2">Add note</div>
        <textarea className="field" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        <button type="button" onClick={addNote} disabled={busy || !note.trim()} className="btn mt-2">Save note</button>
      </div>

      {j.notes?.length > 0 && (
        <div className="mt-6">
          <div className="label mb-2">Notes</div>
          <ul className="space-y-2">
            {j.notes.map((n, i) => (
              <li key={i} className="border-l-2 border-proform-red pl-3 text-sm">
                <div className="label">{n.at?.slice(0, 16).replace('T', ' ')} · {n.by}</div>
                <div>{n.text}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <AuthorizationPanel source={j} sourceType="job" onSent={load} />
    </aside>
  );
}

// ---------------- Modal ----------------
function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink-950/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10" data-testid={testid}>
      <div className="bg-ink-900 border border-ink-700 w-full max-w-2xl p-6 mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display uppercase text-2xl">{title}</div>
          <button onClick={onClose} className="label hover:text-proform-red">Close ✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
